import styles from './PaymentRequestStatusCell.module.css';
import cn from 'classnames';
import {
    creatorbase,
    creatorbaseQueries,
    PostPaymentRequest,
    queryHelpers,
    TiktokInfluencerPost,
    YouTubeInfluencerPost,
} from '@round/api';
import { InstagramInfluencerPost } from '../../../types/Instagram.types';
import { getPaymentStatusLabel } from 'Modules/Plans/Finance/components/PaymentStatusBadge/helpers';
import LegacyPaymentRequestStatusCell from '../legacy/PaymentRequestStatusCell/PaymentRequestStatusCell';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import { Skeleton } from '@round/ui-kit';
import { useIsFetching } from '@tanstack/react-query';

type Props = {
    roundInfluencerPost: TiktokInfluencerPost | InstagramInfluencerPost | YouTubeInfluencerPost;
    paymentRequest?: creatorbase.PostPaymentRequest | null;
    oldFlowPaymentRequest?: PostPaymentRequest | null;
    isGroupShared: boolean | undefined;
    onClickRequestOldFlowPayment: () => void;
    isLoading?: boolean;
    creatorbasePost?: creatorbase.Post | null;
};

const PaymentRequestStatusCell = ({
    roundInfluencerPost,
    creatorbasePost,
    oldFlowPaymentRequest,
    isGroupShared,
    onClickRequestOldFlowPayment,
    isLoading,
}: Props) => {
    const featureFlags = useFeatureFlags();
    // Until we support campaign-level payment requests, we will allow certain users to still use the old flow.
    // An example case is feeder campaigns, where one post payment actually represents multiple posts.
    const isOldFlowEscapeHatchActive = featureFlags.isEnabled('legacy_post_payments');

    /* If the group is shared with creatorbase and no creatorbase post is found, it's likely the user has no team access for the post,
    or something is wrong with the API/data, so we will show a more accurate 'unknown' status rather than interpreting it as unrequested */
    const isPaymentStatusUnverifiable = isGroupShared && !creatorbasePost;

    const doesOldFlowPaymentRequestExist = typeof roundInfluencerPost.payment_request_id === 'number';
    const creatorbasePaymentRequestStatus = creatorbasePost?.payment_request_status;

    const isOldFlowEscapeHatchViable =
        isOldFlowEscapeHatchActive &&
        !doesOldFlowPaymentRequestExist &&
        !isPaymentStatusUnverifiable &&
        (!creatorbasePaymentRequestStatus || !['PAID', 'PENDING'].includes(creatorbasePaymentRequestStatus));

    const isPaymentRequestFetching =
        useIsFetching({
            queryKey: creatorbaseQueries.posts.keys.lists(),
            type: 'active',
            predicate: (query) => {
                const { params } = queryHelpers.parseQueryKey(
                    query.queryKey as queryHelpers.QueryKey<creatorbase.GetPostsParams>
                );
                return Boolean(
                    params.id &&
                        roundInfluencerPost.influencer_post_id &&
                        params.id.split(',').includes(roundInfluencerPost.influencer_post_id)
                );
            },
        }) > 0;

    if (isLoading || isPaymentRequestFetching) {
        return <Skeleton />;
    }

    /*
    We still need to support old-flow payment requests, for historical data display and because they will be used
    in niche cases for a while longer
    */
    if (doesOldFlowPaymentRequestExist || isOldFlowEscapeHatchViable) {
        return (
            <LegacyPaymentRequestStatusCell
                influencerPost={roundInfluencerPost}
                paymentRequest={oldFlowPaymentRequest ?? null}
                onClickRequestPayment={isOldFlowEscapeHatchViable ? onClickRequestOldFlowPayment : undefined}
            />
        );
    }

    const statusLabel = creatorbasePaymentRequestStatus
        ? getPaymentStatusLabel(creatorbasePaymentRequestStatus)
        : isPaymentStatusUnverifiable
          ? 'Unknown'
          : 'Unrequested';

    return (
        <>
            <div className={styles.container} onClick={(e) => e.stopPropagation()}>
                <span
                    className={cn(styles.status, {
                        [styles.paid]: creatorbasePaymentRequestStatus === 'PAID',
                        [styles.pending]: creatorbasePaymentRequestStatus === 'PENDING',
                        [styles.cancelled]: creatorbasePaymentRequestStatus === 'CANCELLED',
                        [styles.failed]: creatorbasePaymentRequestStatus === 'FAILED',
                    })}
                >
                    {statusLabel}
                </span>
            </div>
        </>
    );
};

export default PaymentRequestStatusCell;
